import TileLayer from 'ol/layer/WebGLTile';
import TileSource from 'ol/source/Tile';
import GeomodTileWMS from '../sources/GeomodTileWMS';
import GeomodWMTS from '../sources/GeomodWMTS';
import GeomodTMS from '../sources/GeomodTMS';

import AutoRefreshStrategy from '../utils/AutoRefreshStrategy';
import { S52_FIRST_GROUP_COLORS } from '../utils/S52Colors';
import { LIGHT_CONDITION_PARAM } from '../sources/GeomodSourceUtils';
import { LIGHT_CONDITION } from '../utils/constantes.js'
import { LAYERS_PARAMETERS_UPDATED_EVENT_TYPE } from '../utils/events.js'

/**
 * The options passed to the super class to build it.
 * @type {Array<string>}
 */
const INHERITED_OPTIONS = ['type', 'filter', 'title', 'opacity', 'visible', 'extent', 'minResolution', 'maxResolution', 'preload', 'source', 'map', 'name'];

/**
 * @typedef {Object} GeomodWebGlTileLayerAdditionnalOptions
 * @property {string} [type] If base layer, value is base.
 * @property {string} [filter] Filter name of the layer group in layerswitcher.
 * @property {string} [title] The title of the layer group in the layer switcher.
 * @property {number} [autoRefresh] Duration between to refresh.
 * @property {string} sourceType The type source (TileWMTS or TileWMS).
 * @property {number} [opacity] Opacity (0, 1). Default 1
 * @property {boolean} [visible] Visibility. Default true
 * @property {import('ol/extent').Extent} [extent] The bounding extent for layer rendering. The layer will not be rendered outside of this extent.
 * @property {number} [minResolution] The minimum resolution (inclusive) at which this layer will be visible.
 * @property {number} [maxResolution] The maximum resolution (exclusive) below which this layer will be visible.
 * @property {number} [preload] Preload. Load low-resolution tiles up to preload levels. 0 means no preloading.
 * @property {import('ol/source/Tile').default} [source] Source for this layer.
 * @property {import('ol/PluggableMap').default} [map] Sets the layer as overlay on a map. The map will not manage this layer in its layers collection, and the layer will be rendered on top. This is useful for temporary layers. The standard way to add a layer to a map and have it managed by the map is to use module:ol/Map#addLayer.
 * @property {Map<string,string>} [additionals] Additionals parameters for the layer.
 * @property {string} [repaintStyle] The repaint tile method (BACKGROUND).
 * @property {Array<string>} [repaint] The list of parameters that will trigger repaint.
 */

/**
 * @typedef {GeomodWebGlTileLayerAdditionnalOptions|import("../sources/GeomodWMTS").GeomodWMTSOptions|import("../sources/GeomodTileWMS").GeomodTileWMSOptions} GeomodWebGlTileLayerOptions
 */

/**
 * Change DAY DUSK NIGHT to number value.
 * @enum {Object<string,number>}
 */
const dayDuskNightValues = {
  DAY: 0,
  DUSK: 1,
  NIGHT: 2,
};

export const BACKGROUND_REPAINT_STYLE = 'BACKGROUND';

/**
 * The Geomod tile layer. It create a {@link gm.source.GeomodTileWMS} if source is not provided.
 * @extends {TileLayer}
 */
export default class GeomodWebGlTileLayer extends TileLayer {
  /**
     * @constructor
     * @param {GeomodWebGlTileLayerOptions} options The options to build
     */
  constructor(options = {}) {
    let repaintParametersOptions;
    if (options.repaint) {
      repaintParametersOptions = {};
      const extraParameters = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
      for (const key of options.repaint) {
        repaintParametersOptions[key] = extraParameters[key];
      }
      if (options.repaint.includes(LIGHT_CONDITION_PARAM)) {
        repaintParametersOptions[LIGHT_CONDITION_PARAM] = localStorage.getItem('layerColor') || LIGHT_CONDITION.DAY;
      }
    }

    if (options.source === undefined || !(options.source instanceof TileSource)) {
      switch (options.sourceType) {
        case 'WebGlTileWMTS':
          options.source = new GeomodWMTS(options);
          break;
        case 'WebGlTileTMS':
          options.source = new GeomodTMS(options);
          break;
        case 'WebGlTileWMS':
        default:
          options.source = new GeomodTileWMS(options);
          break;
      }
    }

    let repaintStyle;
    if (options.repaint) repaintStyle = getRepaintStyle(options.repaintStyle);

    const superOptions = {};
    for (const key of INHERITED_OPTIONS) {
      superOptions[key] = options[key];
    }
    if (repaintStyle?.style) superOptions.style = repaintStyle.style;

    super(superOptions);

    this.createRenderer();

    this.repaintParams_ = repaintParametersOptions || {};

    if (repaintStyle) this.toWebGl_ = repaintStyle.variableParser;

    if (options.autoRefresh) {
      this.autoRefresh = new AutoRefreshStrategy(this, options.autoRefresh);
      this.autoRefresh.start();
    }

    if (options.additionals) {
      this.values_ = { ...options.additionals, ...this.values_ };
    }

    if (options.repaint) {
      document.addEventListener(LAYERS_PARAMETERS_UPDATED_EVENT_TYPE, (e) => {
        const extraParameters = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
        for (const key of Object.keys(this.repaintParams_)) {
          if (e.detail.parameters.includes(key)) {
            this.repaintParams_[key] = extraParameters[key];
          }
        }
        this.updateStyleVariables(backgroundParamToWebGlValues(this.repaintParams_));
      })
    }
  }

  /**
     * Add a parameter for the layer.
     * @param {string} paramName Parameter name.
     * @param {string} newValue Parameter value.
     */
  addParam(paramName, value) {
    const source = this.getSource();
    if (source.updateParams) {
      source.updateParams({ [paramName]: value });
    }
  }

  /**
     * Update a parameter if it exists.
     * @param {string} paramName Parameter name.
     * @param {string} newValue Parameter value.
     */
  updateParam(paramName, newValue) {
    const source = this.getSource();
    if (source.updateParams && source.getParams && source.getParams()[paramName]) {
      source.updateParams({ [paramName]: newValue });
    }
    if (Object.keys(this.repaintParams_).includes(paramName)) {
      this.repaintParams_[paramName] = newValue;
      this.updateStyleVariables(backgroundParamToWebGlValues(this.repaintParams_));
    }
  }

  /**
     * Reomve a parameter if it exists.
     * @param {string} paramName Parameter name.
     */
  removeParam(paramName) {
    const source = this.getSource();
    if (source.updateParams && source.getParams) {
      delete source.getParams()[paramName];
      source.updateParams({});
    }
  }

  /**
     * Refresh the layer by requesting a new image in server.
     */
  refresh() {
    this.getSource().refresh();
  }

  /**
     * Get the layer name.
     * @returns {string} the layers name
     */
  getName() {
    let layerName;
    const source = this.getSource();
    if (source instanceof GeomodTileWMS) {
      layerName = source.getParams().LAYERS;
    } else if (source instanceof GeomodWMTS) {
      layerName = source.getLayer();
    } else if (source instanceof GeomodTMS) {
      layerName = source.getName();
    }
    return layerName;
  }

  /**
     * Get the layer tag.
     * @return {string} the layers tag
     */
  getTag() {
    return this.getSource().getTag();
  }
}

/**
 * @typedef {Object} RepaintStyle
 * @property {function(any):Object<string,number>} variableParser The function that turn any object to string=>number object used by webGL
 * @property {import('ol/layer/WebGLTile').Style} style The tile repaint style.
 */

/**
 * Getter to the repaint style.
 * @param {string} styleName The style name.
 * @return {RepaintStyle} The repaint style corresponding to the style name.
 */
function getRepaintStyle(styleName) {
  let repaintStyle;
  switch (styleName) {
    case BACKGROUND_REPAINT_STYLE:
      repaintStyle = {
        variableParser: backgroundParamToWebGlValues,
        style: createBackgroundRepaintStyle(),
      };
      break;
    default:
      repaintStyle = {
        variableParser: (id) => id,
      };
      break;
  }
  return repaintStyle;
}

/**
 * Turn layer params to webGl variables when background style is selected.
 * @param {any} params Layer params.
 * @return {object<string,number>} The webgl variables
 */
function backgroundParamToWebGlValues(params) {
  const [safetyContour, depthContour] = params.SAFETY_CONTOUR;

  return {
    safetyContour: parseFloat(safetyContour),
    depthContour: parseFloat(depthContour),
    tide: parseFloat(safetyContour - depthContour),
    lightCondition: dayDuskNightValues[params[LIGHT_CONDITION_PARAM]],
  };
}

/**
 * Creates the background style.
 * @return {import('ol/layer/WebGLTile').Style} The WebGl style to repaint background layer.
 */
function createBackgroundRepaintStyle() {
  /*
     * This methode return alogrithm equivalent to this code (in WebGl)
     *  let pixel = pixels[0];
     *  if(pixel[3] !== 0 && (pixel[2] === 0 || pixel[2] === 1) ) {
     *      // if blue pixel is 0 or 1
     *      let drval1;
     *      if(pixel[2] === 0) {
     *          drval1 = pixel[0] + pixel[1] / 10;
     *      } else if(pixel[2] === 1) {
     *          drval1 = - pixel[0] - pixel[1] / 10;
     *      }
     *
     *      if(drval1 >= data.depthContour) {
     *          pixel = S52_CO_DEPDW[data.lightCondition];
     *      } else if(drval1 >= data.safetyContour) {
     *          pixel = S52_CO_DEPMD[data.lightCondition];
     *      } else if(drval1 >= data.tide) {
     *          pixel = S52_CO_DEPVS[data.lightCondition];
     *      } else {
     *          pixel = S52_CO_DEPIT[data.lightCondition];
     *      }
     *  }
     */

  const drval1 = [
    'case',
    ['==', ['band', 3], 0],
    [
      '+',
      ['*', 255, ['band', 1]],
      ['*', ['band', 2], 25.5],
    ],
    [
      '-',
      ['*', ['band', 1], -255],
      ['*', ['band', 2], 25.5],
    ],
  ];


  /**
   * return value
   * if (alpha === 0 || blue >= 2/255) => color pixel rapporté a 255
   * else if (drval1 >= depthContour) => S52_CO_DEPDW (DAY, DUSK, NIGHT)
   * else if (drval1 >= safetyContour) => S52_CO_DEPMD (DAY, DUSK, NIGHT)
   * else if (drval1 >= tide) => S52_CO_DEPVS (DAY, DUSK, NIGHT)
   * else if (lightCond === DAY) => S52_CO_DEPIT.DAY
   * else if (lightCond === DUSK) => S52_CO_DEPIT.DUSK
   * else S52_CO_DEPIT.NIGHT
   */
  return {
    variables: {
      depthContour: 0,
      safetyContour: 0,
      tide: 0,
      lightCondition: 0,
    },
    color: [
      // need repaint or not
      'case',
      // alpha == 0 || blue >= 2 => let the pixel color.
      [
        'any',
        ['==', ['band', 4], 0], // Initial color is transparant
        ['>=', ['band', 3], 2 / 255], // Initial blue value is not 0 or 1
      ],
      // The original color
      [
        'color',
        ['*', 255, ['band', 1]],
        ['*', 255, ['band', 2]],
        ['*', 255, ['band', 3]],
        ['band', 4],
      ],
      // deeper than selected deep contour | eau saine
      ...selectColor(['>=', drval1, ['var', 'depthContour']], S52_FIRST_GROUP_COLORS.S52_CO_DEPDW),
      // safety contour to selected deep contour | eau saine grace a la mare
      ...selectColor(['>=', drval1, ['var', 'safetyContour']], S52_FIRST_GROUP_COLORS.S52_CO_DEPMD),
      // zero meter contour to shallow contour | eau malsaine
      ...selectColor(['>=', drval1, ['var', 'tide']], S52_FIRST_GROUP_COLORS.S52_CO_DEPVS),
      // ...selectColor(['>=', drval1, ['var', 'tide']], S52_FIRST_GROUP_COLORS.S52_CO_DEPMS),
      // ...selectColor(['>=', drval1, 0], S52_FIRST_GROUP_COLORS.S52_CO_DEPVS),
      // else | estran
      ['==', ['var', 'lightCondition'], dayDuskNightValues.DAY], // DAY
      toColorAlphaPurcent(S52_FIRST_GROUP_COLORS.S52_CO_DEPIT.DAY),
      ['==', ['var', 'lightCondition'], dayDuskNightValues.DUSK], // DUSK
      toColorAlphaPurcent(S52_FIRST_GROUP_COLORS.S52_CO_DEPIT.DUSK),
      toColorAlphaPurcent(S52_FIRST_GROUP_COLORS.S52_CO_DEPIT.NIGHT), // fallback estan night color
    ],
  };

  /**
     * Create case condition to select color according to the ligthCondition (DAY, DUSK, NIGHT)
     * @param {Array<any>} condition The condition except the color.
     * @param {Object<string,Array<number>} color the S52 color.
     * @return {Array<Array<any>>}
     */
  function selectColor(condition, color) {
    /**
     * return value
     * if (lightCond === DAY && condition verified) => toColorAlphaPurcent(color.DAY),
     * else if (lightCond === DUSK && condition verified) => toColorAlphaPurcent(color.DUSK)
     * else if (lightCond === NIGHT && condition verified) => toColorAlphaPurcent(color.NIGHT)
     */
    return [
      [
        'all',
        ['==', ['var', 'lightCondition'], dayDuskNightValues.DAY],
        condition,
      ],
      toColorAlphaPurcent(color.DAY),
      [
        'all',
        ['==', ['var', 'lightCondition'], dayDuskNightValues.DUSK],
        condition,
      ],
      toColorAlphaPurcent(color.DUSK),
      [
        'all',
        ['==', ['var', 'lightCondition'], dayDuskNightValues.NIGHT],
        condition,
      ],
      toColorAlphaPurcent(color.NIGHT),
    ];
  }

  /**
     * Transform olColor alpha to 1.
     * @param {import('ol/color').Color} color The olColor
     * @return The olColor with alpha value to 1
     */
  function toColorAlphaPurcent(color) {
    const newColor = [...color];
    newColor[3] = 1;
    return newColor;
  }
}
